import { Button, Input, Table, TableProps, Typography, message } from 'antd'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import StudentAvatar from '~/components/StudentAvatar'
import { getOrder } from '~/services/card'
import { ICard } from '~/types/card'

import { Container } from './style'
import ModalForm from './ModalForm'

const Index = () => {
  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState<ICard[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [queryPhone, setQueryPhone] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentCard, setCurrentCard] = useState<Partial<ICard> | null>(null)

  const onPageChange = useCallback((cPage: number, cSize: number) => {
    setPage(cPage)
    setSize(cSize)
  }, [])
  const fetchData = useCallback(() => {
    setLoading(true)
    getOrder(page, size, queryPhone || undefined)
      .then((res) => {
        setTotal(res.data?.total || 0)
        setDataSource(res.data?.content || [])
      })
      .catch((err) => {
        message.error(err?.message || '请求错误，请稍后再试')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [page, size, queryPhone])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSearch = useCallback(() => {
    const trimmedPhone = phoneNumber.trim()
    const needResetPage = page !== 1
    const needUpdateQuery = queryPhone !== trimmedPhone

    if (needResetPage) {
      setPage(1)
    }
    if (needUpdateQuery) {
      setQueryPhone(trimmedPhone)
    }

    if (!needResetPage && !needUpdateQuery) {
      fetchData()
    }
  }, [fetchData, page, phoneNumber, queryPhone])

  const columns = useRef<TableProps<ICard>['columns']>([
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      fixed: 'left',
    },
    {
      title: '学生ID',
      dataIndex: 'studentId',
      key: 'studentId',
      width: 150,
    },
    {
      title: '学生',
      dataIndex: 'student',
      key: 'student',
      width: 160,
      render: (_, record) => <StudentAvatar student={record.student} showName />,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 100,
    },
    {
      title: '过期时间',
      dataIndex: 'expiredTime',
      key: 'expiredTime',
      width: 200,
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '总课时',
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: 120,
    },
    {
      title: '已使用',
      key: 'usedCount',
      width: 120,
      render: (_, record) =>
        Math.max(record.totalCount - record.count, 0),
    },
    {
      title: '剩余课时',
      dataIndex: 'count',
      key: 'count',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 150,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Typography.Link
          onClick={() => {
            setCurrentCard(record)
            setIsModalOpen(true)
          }}
        >
          编辑
        </Typography.Link>
      ),
    },
  ])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setCurrentCard(null)
  }, [])

  return (
    <Container>
      <div className="top">
        <Input
          placeholder="请输入学生手机号"
          style={{ width: 220, marginRight: 12 }}
          allowClear
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
        <Button
          style={{ marginRight: 12 }}
          type="primary"
          loading={loading}
          onClick={onSearch}
        >
          查询
        </Button>
      </div>
      <div className="table">
        <Table
          rowKey="id"
          scroll={{ y: 500 }}
          loading={loading}
          columns={columns.current}
          dataSource={dataSource}
          pagination={{
            total,
            pageSize: size,
            current: page,
            onChange: onPageChange,
          }}
        />
      </div>
      <div className="modal">
        {currentCard && (
          <ModalForm
            open={isModalOpen}
            data={currentCard}
            onClose={closeModal}
            refreshTable={fetchData}
          />
        )}
      </div>
    </Container>
  )
}

export default memo(Index)
